import React, { FC } from "react";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { FeatureGate } from "../../../../branding/FeatureGate";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { redirect } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import { RechargeCreditCardDto } from "types/api/recharge";
import { OpenRightPanel } from "types/common/rightPanel";
import { PaymentFooter } from "../../../../components/PaymentButton";
import {
  createRechargeBalance,
  updateAmountFunds,
} from "../../../../reduxFolder/reducers/books";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  authCreditCardFeePercent,
  authSelectUser,
} from "../../../../reduxFolder/selectors/globalSelectors";
import formatter from "../../../../utils/formatter";
import {
  PaymentFormButtonSlot,
  PaymentFormFooter,
} from "../../../../styles/paymentFormStyles";
import { getRestorableCreditCardDetailsValues } from "../../../../components/CreditCardForm/creditCardFormHelper";
import addFundsFormik from "../../helpers/addFundsFormik";
import {
  BooksBalanceLayout,
  BooksBalancePanelSection,
  ContentContainer,
  FormWrapper,
} from "../../styles/BooksAddFunds.style";
import BooksBalance from "../BooksBalance/BooksBalance";
import BooksAddFundsForm from "./BooksAddFundsForm";

interface BooksAddFundsPanelProps {
  panelData: any;
  createRechargeBalance: ({ data }: RechargeCreditCardDto) => Promise<void>;
  updateAmountFunds: (amount: string) => void;
  openRightPanel: OpenRightPanel;
  authCreditCardFeePercent: string;
  user: any;
}

const BooksAddFundsPanel: FC<BooksAddFundsPanelProps> = React.memo(
  ({
    panelData,
    createRechargeBalance,
    updateAmountFunds,
    openRightPanel,
    authCreditCardFeePercent,
    user,
  }) => {
    const { t, i18n } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const redirectFunc = () => redirect("/parcels");
    const restoredValues = panelData.get("formValues");

    const formik = useFormik(
      addFundsFormik(
        t,
        createRechargeBalance,
        redirectFunc,
        updateAmountFunds,
        i18n.language,
        user.get("country", "US"),
        currencySymbol,
        restoredValues,
      ),
    );
    const {
      handleSubmit,
      values,
      isSubmitting,
      isValid,
    } = formik;

    const { amount } = values;
    const amountValue = parseFloat(amount && amount !== "" ? amount : "0");
    const amountFloat = Number.isFinite(amountValue) ? amountValue : 0;
    const creditCardFeePercent = parseFloat(authCreditCardFeePercent);
    const commissionFeeFloat = Number.isFinite(creditCardFeePercent)
      ? creditCardFeePercent
      : 0;
    const commissionFeeValue =
      amountFloat > 0
        ? formatter.roundTo2Decimal(commissionFeeFloat * amountFloat)
        : 0;
    const total = formatter.roundTo2Decimal(amountFloat + commissionFeeValue);
    const helpPanelContext = {
      openRightPanel,
      returnPanelType: "BOOKS_ADD_FUNDS",
      getReturnPanelData: () =>
        panelData.set("formValues", {
          amount: formik.values.amount,
          address: {
            ...formik.values.address,
          },
          cardDetails: getRestorableCreditCardDetailsValues(
            formik.values.cardDetails,
          ),
        }),
    };

    const formattedTotal = `${currencySymbol}${formatter.currency(total)}`;
    const payBtnTitle = `${t("common.processBankCard")} (${formattedTotal})`;

    return (
      <FeatureGate payment="credit_card">
        <ContentWrapper>
          <Heading $isBordered>
            {t("books.addFundsPanel.chargeBankCard")}
          </Heading>
          <ContentContainer>
            <FormWrapper>
              <BooksBalancePanelSection>
                <BooksBalanceLayout>
                  <BooksBalance balance={user.get("balance")} />
                </BooksBalanceLayout>
              </BooksBalancePanelSection>
              <BooksAddFundsForm formik={formik} />
            </FormWrapper>
            <PaymentFormFooter>
              <PaymentFormButtonSlot>
                <PaymentFooter
                  onClick={() => handleSubmit()}
                  isLoading={isSubmitting}
                  disabled={!isValid || isSubmitting}
                  helpPanelContext={helpPanelContext}
                >
                  {payBtnTitle}
                </PaymentFooter>
              </PaymentFormButtonSlot>
            </PaymentFormFooter>
          </ContentContainer>
        </ContentWrapper>
      </FeatureGate>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    authCreditCardFeePercent,
    user: authSelectUser,
  }),
  {
    createRechargeBalance,
    updateAmountFunds,
    openRightPanel,
  },
);

export default withConnect(BooksAddFundsPanel);
